import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { CustomButton } from '../../../components/CustomButton';
import TableActionsContainer from '../../../components/CustomTable/TableActionsContainer';
import Loader from '../../../components/Loader';
import MetadataDropdown from '../../../components/MetaData-Selector';
import { Text } from '../../../components/Text';
import { useUtility } from '../../../context/UtilityContext';
import { getWhatsAppTemplateLibrary_WCA } from '../../../reducers/wca/template/template-action';
import CampaignTemplatePreview from './CampaignTemplatePreview';

const TOPIC_VALUES = [
  'ALL',
  'ACCOUNT_UPDATES',
  'CALL_PERMISSIONS',
  'CUSTOMER_FEEDBACK',
  'EVENT_REMINDER',
  'IDENTITY_VERIFICATION',
  'ORDER_MANAGEMENT',
  'PAYMENTS',
  'PUBLIC_SAFETY',
];

const USE_CASE_VALUES = [
  'ALL',
  'ACCOUNT_CREATION_CONFIRMATION',
  'APPOINTMENT_CANCELLATION',
  'APPOINTMENT_CONFIRMATION',
  'APPOINTMENT_REMINDER',
  'APPOINTMENT_SCHEDULEING',
  'AUTO_PAY_REMINDER',
  'CALL_PERMISSION_REQUEST',
  'CRISIS_RESPONSE',
  'DELIVERY_CONFIRMATION',
  'DELIVERY_FAILED',
  'DELIVERY_UPDATE',
  'FEEDBACK_SURVEY',
  'FRAUD_ALERT',
  'IN_PERSON_VERIFICATION',
  'LOW_BALANCE_WARNING',
  'ORDER_ACTION_NEEDED',
  'ORDER_CONFIRMATION',
  'ORDER_DELAY',
  'ORDER_OR_TRANSACTION_CANCEL',
  'ORDER_PICK_UP',
  'PAYMENT_ACTION_REQUIRED',
  'PAYMENT_CONFIRMATION',
  'PAYMENT_DUE_REMINDER',
  'PAYMENT_OVERDUE',
  'PAYMENT_REJECT_FAIL',
  'PAYMENT_SCHEDULED',
  'RECEIPT_ATTACHMENT',
  'RETURN_CONFIRMATION',
  'SHIPMENT_CONFIRMATION',
  'STATEMENT_ATTACHMENT',
  'STATEMENT_AVAILABLE',
  'TRANSACTION_ALERT',
];

const INDUSTRY_VALUES = ['ALL', 'E_COMMERCE', 'FINANCIAL_SERVICES', 'TELECOMMUNICATION'];

const languageOptions = [{ label: 'English (en)', value: 'en' }];

const filterLabelStyle = {
  display: 'block',
  marginBottom: '0.5rem',
  color: '#374151',
  fontSize: '0.85rem',
  fontWeight: 600,
};

const formatLibraryLabel = (value) => {
  if (!value) return '';

  const customLabels = {
    ALL: 'All',
    E_COMMERCE: 'E-Commerce',
    FINANCIAL_SERVICES: 'Financial Services',
    TELECOMMUNICATION: 'Telecommunication',
    IN_PERSON_VERIFICATION: 'In-Person Verification',
  };

  if (customLabels[value]) {
    return customLabels[value];
  }

  return value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const buildOptions = (values) =>
  values.map((value) => ({
    label: formatLibraryLabel(value),
    value,
  }));

const topicOptions = buildOptions(TOPIC_VALUES);
const useCaseOptions = buildOptions(USE_CASE_VALUES);
const industryOptions = buildOptions(INDUSTRY_VALUES);

const formatValueForDisplay = (value, formatter = formatLibraryLabel) => {
  if (value === undefined || value === null || String(value).trim() === '') {
    return '--';
  }

  return formatter(String(value));
};

const formatLanguageLabel = (value) => {
  const matchedLanguage = languageOptions.find((option) => option.value === value);
  return matchedLanguage?.label || formatValueForDisplay(value);
};

const formatIndustryList = (industries = []) => {
  if (!Array.isArray(industries) || industries.length === 0) {
    return '--';
  }

  return industries.map((industry) => formatLibraryLabel(industry)).join(', ');
};

const WCATemplateLibrary = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { selectedMetadataId } = useUtility();
  const [searchValue, setSearchValue] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [selectedTopic, setSelectedTopic] = useState('ALL');
  const [selectedUseCase, setSelectedUseCase] = useState('ALL');
  const [selectedIndustry, setSelectedIndustry] = useState('ALL');
  const [selectedTemplatePreview, setSelectedTemplatePreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const {
    templateLibraryList,
    templateLibraryCursor,
    templateLibraryLoading,
    templateLibraryError,
  } = useSelector((state) => state.wcaMessageTemplate);

  const templateLibraryFilters = useMemo(
    () => ({
      metadataid: selectedMetadataId,
      search: searchValue.trim(),
      topic: selectedTopic,
      usecase: selectedUseCase,
      industry: selectedIndustry,
      language: selectedLanguage,
    }),
    [
      searchValue,
      selectedIndustry,
      selectedLanguage,
      selectedMetadataId,
      selectedTopic,
      selectedUseCase,
    ],
  );

  useEffect(() => {
    if (!templateLibraryFilters.metadataid) return;

    const fetchTemplateLibrary = async () => {
      try {
        const response = await dispatch(
          getWhatsAppTemplateLibrary_WCA({
            ...templateLibraryFilters,
            after: '',
            append: false,
          }),
        ).unwrap();
      } catch (error) {
        console.error('WhatsApp template library fetch failed:', error);
      }
    };

    fetchTemplateLibrary();
  }, [dispatch, templateLibraryFilters]);

  const handlePreview = (template) => {
    setSelectedTemplatePreview(template);
    setPreviewOpen(true);
  };

  const handleUseTemplate = (template) => {
    navigate('/campaign-templates/new', {
      state: {
        templateLibraryTemplate: template,
      },
    });
  };

  const handleLoadMore = () => {
    if (!templateLibraryCursor || !selectedMetadataId || templateLibraryLoading) return;

    dispatch(
      getWhatsAppTemplateLibrary_WCA({
        ...templateLibraryFilters,
        after: templateLibraryCursor,
        append: true,
      }),
    )
      .unwrap()
      .then((response) => {})
      .catch((error) => {
        console.error('WhatsApp template library load more failed:', error);
      });
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex align-items-center gap-2">
        <Button
          icon="pi pi-eye"
          className="p-button-text p-button-sm"
          onClick={() => handlePreview(rowData)}
          tooltip="Preview"
        />
        <Button
          icon="pi pi-check"
          className="p-button-text p-button-sm"
          onClick={() => handleUseTemplate(rowData)}
          tooltip="Use"
        />
      </div>
    );
  };

  return (
    <div className="m-3">
      <Loader visible={templateLibraryLoading} />

      <TableActionsContainer>
        <div className="flex justify-content-between align-items-center gap-3 flex-wrap">
          <Text type="heading">Template Library</Text>
          <MetadataDropdown showLabel={false} />
        </div>
      </TableActionsContainer>

      <div className="px-3 pt-3">
        <div className="flex gap-3 flex-nowrap overflow-x-auto pb-2">
          <div style={{ minWidth: '18rem' }}>
            <label style={filterLabelStyle}>Search</label>
            <span className="p-input-icon-left w-full">
              <i className="pi pi-search" />
              <InputText
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Search templates"
                className="w-full"
              />
            </span>
          </div>

          <div style={{ minWidth: '14rem' }}>
            <label style={filterLabelStyle}>Language</label>
            <Dropdown
              value={selectedLanguage}
              options={languageOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setSelectedLanguage(e.value)}
              className="w-full"
            />
          </div>

          <div style={{ minWidth: '14rem' }}>
            <label style={filterLabelStyle}>Topic</label>
            <Dropdown
              value={selectedTopic}
              options={topicOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setSelectedTopic(e.value)}
              className="w-full"
              filter
            />
          </div>

          <div style={{ minWidth: '14rem' }}>
            <label style={filterLabelStyle}>Use Case</label>
            <Dropdown
              value={selectedUseCase}
              options={useCaseOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setSelectedUseCase(e.value)}
              className="w-full"
              filter
            />
          </div>

          <div style={{ minWidth: '14rem' }}>
            <label style={filterLabelStyle}>Industry</label>
            <Dropdown
              value={selectedIndustry}
              options={industryOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setSelectedIndustry(e.value)}
              className="w-full"
              filter
            />
          </div>
        </div>
      </div>

      {templateLibraryError && (
        <div className="px-3 pt-2 text-red-500 text-sm">{String(templateLibraryError)}</div>
      )}

      <div className="px-3 pt-3">
        <div className="flex justify-content-between align-items-center mb-2">
          <Text type="sub-heading">Total Templates: {templateLibraryList.length}</Text>
        </div>

        <div className="border-round overflow-x-auto">
          <DataTable
            value={templateLibraryList}
            dataKey="id"
            className="skalebot-table"
            removableSort
            emptyMessage="No templates found"
          >
            <Column
              field="name"
              header="Name"
              sortable
              style={{ minWidth: '12rem', width: '12rem' }}
              body={(rowData) => (
                <div style={{ wordBreak: 'break-word' }}>{rowData?.name || '--'}</div>
              )}
            />
            <Column
              field="body"
              header="Description"
              body={(rowData) => (
                <div style={{ whiteSpace: 'pre-line', minWidth: '24rem' }}>
                  {rowData?.body || '--'}
                </div>
              )}
            />
            <Column
              field="language"
              header="Language"
              body={(rowData) => formatLanguageLabel(rowData?.language)}
            />
            <Column
              field="topic"
              header="Topic"
              body={(rowData) => formatValueForDisplay(rowData?.topic)}
            />
            <Column
              field="usecase"
              header="Use Case"
              body={(rowData) => formatValueForDisplay(rowData?.usecase)}
            />
            <Column
              field="industry"
              header="Industry"
              body={(rowData) => formatIndustryList(rowData?.industry)}
            />
            <Column
              header="Action"
              body={actionBodyTemplate}
              style={{ minWidth: '8rem', width: '8rem' }}
            />
          </DataTable>
        </div>

        {templateLibraryCursor && (
          <div className="flex justify-content-center pt-3">
            <CustomButton
              varient="outline"
              label={templateLibraryLoading ? 'Loading...' : 'Load More'}
              onClick={handleLoadMore}
              disabled={templateLibraryLoading}
            />
          </div>
        )}
      </div>

      <CampaignTemplatePreview
        visible={previewOpen}
        onClose={setPreviewOpen}
        data={selectedTemplatePreview}
      />
    </div>
  );
};

export default WCATemplateLibrary;
